<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">LINE 圖文選單</h1>
      </div>
      <div class="header-actions">
        <router-link v-if="activeTab === 'list'" to="/client/line/rich-menu/create" class="btn btn-primary">
          <i class="ri-add-line"></i> 新增圖文選單
        </router-link>
      </div>
    </div>

    <div class="page-container">
    <!-- 圖文選單說明 -->
    <div class="rich-menu-intro" :class="{ 'is-expanded': introOpen }">
      <button type="button" class="intro-toggle" @click="introOpen = !introOpen">
        <span class="intro-title">
          <i class="ri-information-line"></i>
          圖文選單的使用說明
        </span>
        <span class="intro-toggle-action">
          <span class="intro-toggle-label">{{ introOpen ? '收起' : '查看說明' }}</span>
          <i :class="introOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
        </span>
      </button>
      <div v-show="introOpen" class="intro-body">
        <p class="intro-text">LINE 圖文選單顯示在聊天室底部，可設定多個 Tab 與點擊區塊，引導好友前往功能或網頁。本系統提供兩種顯示模式，請依需求在「LINE 設定」擇一啟用。</p>
        <ul class="intro-list">
          <li>
            <strong>排程模式</strong>：依開始／結束時間，自動對<strong>所有好友</strong>顯示同一套選單。適合節慶活動、限時促銷等全員看到的檔期（例如母親節 5/1～5/15 活動選單）。未設定時間時需手動「立即套用」才會生效；指定「基本選單」會立即取代目前預設，並作為活動結束後的備援選單。
          </li>
          <li>
            <strong>會員模式</strong>：依<strong>會員等級</strong>指派不同選單，不同等級的好友看到不同內容（例如 VIP 專屬選單、一般會員選單）。需為各等級及「非會員」分別建立選單，完成後按「更新部署」同步指派給符合等級的好友。
          </li>
          <li><strong>提示</strong>：兩種模式擇一使用，切換後列表僅顯示該模式的選單；草稿需「啟用」並「立即套用」後，好友才能在 LINE 看到。</li>
        </ul>
      </div>
    </div>

    <!-- 頁面 Tab -->
    <div class="page-tabs">
      <button :class="['page-tab', { active: activeTab === 'list' }]" @click="activeTab = 'list'">
        <i class="ri-layout-grid-line"></i> 選單列表
      </button>
      <button :class="['page-tab', { active: activeTab === 'logs' }]" @click="switchToLogs">
        <i class="ri-history-line"></i> 操作記錄
      </button>
    </div>

    <!-- 當前模式橫幅 -->
    <div v-show="activeTab === 'list'" v-if="richMenuMode" :class="['mode-banner', richMenuMode]">
      <i :class="richMenuMode === 'schedule' ? 'ri-calendar-schedule-line' : 'ri-vip-crown-line'"></i>
      <span>
        目前使用
        <strong>{{ richMenuMode === 'schedule' ? '排程模式（一般模式）' : '會員模式' }}</strong>
        ，僅顯示該模式的選單
      </span>
      <button
        v-if="richMenuMode === 'member'"
        class="btn-batch-deploy"
        :disabled="batchDeploying"
        @click="handleBatchDeploy"
      >
        <i :class="batchDeploying ? 'ri-loader-4-line spin' : 'ri-rocket-line'"></i>
        {{ batchDeploying ? '部署中…' : '更新部署' }}
      </button>
      <router-link to="/client/settings?tab=line" class="mode-banner-link">
        前往設定
        <i class="ri-arrow-right-s-line"></i>
      </router-link>
    </div>

    <!-- 批次部署進度 Modal -->
    <div v-if="deployJob" class="batch-result-backdrop" @click.self="deployJob.status === 'completed' && closeBatchModal()">
      <div class="batch-result-modal">
        <div class="batch-result-header">
          <template v-if="deployJob.status === 'processing'">
            <i class="ri-loader-4-line spin" style="color:#6366f1"></i>
            <h3>批次部署進行中</h3>
          </template>
          <template v-else>
            <i :class="deployJobHasError ? 'ri-error-warning-fill warn-icon' : 'ri-checkbox-circle-fill success-icon'"></i>
            <h3>批次部署完成</h3>
          </template>
        </div>

        <!-- 選單結構部署進度 -->
        <div class="deploy-stage">
          <span class="stage-label">選單結構部署</span>
          <span :class="['stage-badge', deployJob.done_groups >= deployJob.total_groups ? 'done' : 'pending']">
            {{ deployJob.done_groups }} / {{ deployJob.total_groups }} 完成
          </span>
        </div>

        <!-- 用戶指派進度 -->
        <div class="deploy-stage" v-if="deployJob.processed_users > 0 || deployJob.status === 'completed'">
          <span class="stage-label">會員指派</span>
          <span class="stage-badge done">已處理 {{ deployJob.processed_users.toLocaleString() }} 位好友</span>
        </div>
        <div class="deploy-stage-note" v-else-if="deployJob.status === 'processing'">
          <i class="ri-information-line"></i>
          會員指派由背景任務處理，數量龐大時需數分鐘至數小時，可關閉此視窗繼續操作
        </div>

        <!-- 各選單狀態列表 -->
        <div class="batch-result-list">
          <div
            v-for="r in (deployJob.group_results || [])"
            :key="r.groupId"
            :class="['batch-result-item', r.status === 'success' ? 'success' : r.status === 'error' ? 'error' : 'pending']"
          >
            <i :class="r.status === 'success' ? 'ri-checkbox-circle-line' : r.status === 'error' ? 'ri-close-circle-line' : 'ri-loader-4-line spin'"></i>
            <span class="r-name">{{ r.name }}</span>
            <span v-if="r.error" class="r-msg">{{ r.error }}</span>
            <span v-else-if="r.status === 'pending'" class="r-msg">等待中</span>
          </div>
        </div>

        <div class="batch-result-footer">
          <button v-if="deployJob.status === 'processing'" class="btn btn-secondary" @click="closeBatchModal()">
            背景執行，關閉視窗
          </button>
          <button v-else class="btn btn-primary" @click="closeBatchModal(); loadList()">
            確定
          </button>
        </div>
      </div>
    </div>

    <!-- 會員模式完成度提醒 -->
    <div v-if="memberCompleteness" :class="['completeness-card', memberCompleteness.allDone ? 'all-done' : 'has-missing']">
      <div class="completeness-header">
        <i :class="memberCompleteness.allDone ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'"></i>
        <span class="completeness-title">
          {{ memberCompleteness.allDone ? '所有必要選單已建立完成' : '部分必要選單尚未建立' }}
        </span>
        <span class="completeness-count">
          {{ memberCompleteness.doneCount }} / {{ memberCompleteness.total }} 完成
        </span>
      </div>
      <div class="completeness-desc" v-if="!memberCompleteness.allDone">
        會員模式需為每個會員等級建立選單，並額外建立一個「非會員」選單（未設定等級），部署後系統自動依等級分配。
      </div>
      <div class="completeness-list">
        <div :class="['completeness-item', memberCompleteness.hasNonMemberMenu ? 'done' : 'missing']">
          <i :class="memberCompleteness.hasNonMemberMenu ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'"></i>
          <span>非會員選單</span>
          <span class="item-hint">{{ memberCompleteness.hasNonMemberMenu ? '已建立' : '尚未建立（不設定等級的選單）' }}</span>
        </div>
        <div
          v-for="check in memberCompleteness.tierChecks"
          :key="check.id"
          :class="['completeness-item', check.done ? 'done' : 'missing']"
        >
          <i :class="check.done ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'"></i>
          <span>{{ check.name }}</span>
          <span class="item-hint">{{ check.done ? '已建立' : '尚未建立' }}</span>
        </div>
      </div>
    </div>

    <!-- 篩選工具列 -->
    <div v-show="activeTab === 'list'" class="page-toolbar">
      <div class="search-box">
        <input
          v-model="filters.keyword"
          type="text"
          class="form-control"
          placeholder="搜尋選單名稱"
          @keyup.enter="search"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filters.status" @change="search" class="form-control">
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="active">已建立</option>
          <option value="expired">已到期</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filters.lineConfigId" @change="search" class="form-control">
          <option value="">全部帳號</option>
          <option v-for="c in lineConfigs" :key="c.id" :value="c.id">{{ c.configName }}</option>
        </select>
      </div>
      <button class="btn btn-secondary" @click="search">
        <i class="ri-refresh-line"></i> 重新整理
      </button>
    </div>

    <!-- 目前顯示中的選單 -->
    <div v-show="activeTab === 'list'" v-if="currentMenu" class="current-menu-card">
      <div class="current-menu-label">
        <i class="ri-broadcast-fill"></i> 目前顯示中
      </div>
      <div class="current-menu-body">
        <div class="current-menu-thumb-wrap">
          <img
            v-if="currentMenu.firstTabImageUrl"
            :src="currentMenu.firstTabImageUrl"
            :class="['current-menu-thumb', currentMenu.firstTabHeightType === 'half' ? 'half' : 'full']"
            alt=""
          />
          <span v-else class="current-menu-thumb-placeholder">
            <i class="ri-image-line"></i>
          </span>
        </div>
        <div class="current-menu-info">
          <div class="current-menu-name">{{ currentMenu.name }}</div>
          <div class="current-menu-meta">
            <span>{{ getConfigName(currentMenu.lineConfigId) }}</span>
            <span class="dot">·</span>
            <span>{{ currentMenu.displayMode === 'schedule' ? '排程模式' : '會員模式' }}</span>
            <span class="dot">·</span>
            <span>{{ currentMenu.tabCount }} 個 Tab</span>
            <template v-if="currentMenu.isBaseMenu">
              <span class="dot">·</span>
              <span class="base-tag"><i class="ri-home-heart-fill"></i> 基本選單</span>
            </template>
          </div>
          <div
            v-if="currentMenu.isBaseMenu || currentMenu.startDate || currentMenu.endDate"
            class="current-menu-dates"
          >
            <template v-if="currentMenu.isBaseMenu && !currentMenu.startDate && !currentMenu.endDate">
              <span class="date-item">
                <i class="ri-flashlight-line"></i>
                <span class="date-label">生效方式</span>
                <span class="date-value">立即生效</span>
              </span>
              <span class="date-item">
                <i class="ri-infinity-line"></i>
                <span class="date-label">有效期間</span>
                <span class="date-value">無期限</span>
              </span>
            </template>
            <span v-if="currentMenu.startDate" class="date-item">
              <i class="ri-arrow-up-circle-line"></i>
              <span class="date-label">上架時間</span>
              <span class="date-value">{{ formatDate(currentMenu.startDate) }}</span>
            </span>
            <span v-if="currentMenu.endDate" class="date-item">
              <i class="ri-arrow-down-circle-line"></i>
              <span class="date-label">預計下架</span>
              <span class="date-value">{{ formatDate(currentMenu.endDate) }}</span>
              <span class="remaining-badge">{{ remainingTime(currentMenu.endDate) }}</span>
            </span>
          </div>
        </div>
        <div class="current-menu-actions">
          <router-link :to="`/client/line/rich-menu/${currentMenu.id}/edit`" class="btn btn-sm btn-primary">
            <i class="ri-edit-line"></i> 編輯
          </router-link>
          <button
            class="btn btn-sm btn-undeploy"
            @click="handleUndeploy(currentMenu)"
            :disabled="deploying === currentMenu.id"
          >
            <i :class="deploying === currentMenu.id ? 'ri-loader-4-line spin' : 'ri-stop-circle-line'"></i>
            {{ deploying === currentMenu.id ? '處理中' : '轉為草稿' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 列表 -->
    <div v-show="activeTab === 'list'" class="table-wrapper">
      <div v-if="loading" class="loading-state">
        <i class="ri-loader-4-line spin"></i> 載入中...
      </div>
      <table v-else-if="items.length > 0">
        <thead>
          <tr>
            <th>選單名稱</th>
            <th>LINE 帳號</th>
            <th>顯示模式</th>
            <th>Tab 數</th>
            <th>點擊數</th>
            <th>有效期間</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td class="name-cell">
              <div class="menu-thumb-wrap">
                <img
                  v-if="item.firstTabImageUrl"
                  :src="item.firstTabImageUrl"
                  :class="['menu-thumb', item.firstTabHeightType === 'half' ? 'half' : 'full']"
                  alt=""
                />
                <span v-else :class="['menu-thumb-placeholder', item.firstTabHeightType === 'half' ? 'half' : 'full']">
                  <i class="ri-image-line"></i>
                </span>
                <span class="menu-name">{{ item.name }}</span>
              </div>
            </td>
            <td>{{ getConfigName(item.lineConfigId) }}</td>
            <td>
              <span :class="['mode-badge', item.displayMode]">
                <i :class="item.displayMode === 'schedule' ? 'ri-calendar-line' : 'ri-vip-crown-line'"></i>
                {{ item.displayMode === 'schedule' ? '排程模式' : '會員模式' }}
              </span>
            </td>
            <td class="center-cell">{{ item.tabCount }} 個</td>
            <td class="clicks-cell">
              <template v-if="item.trackingEnabled">
                <strong>{{ formatCount(item.totalClicks) }}</strong>
                <span>{{ formatCount(item.totalUniqueClicks) }} 人</span>
              </template>
              <span v-else class="tracking-disabled-badge">尚未啟用點擊追蹤</span>
            </td>
            <td class="date-cell">
              <template v-if="item.displayMode === 'schedule'">
                <span v-if="item.isBaseMenu && !item.startDate && !item.endDate" class="base-period-label">
                  立即生效／無期限
                </span>
                <span v-else-if="item.startDate">{{ formatDate(item.startDate) }}</span>
                <span v-else class="text-muted">—</span>
                <span v-if="item.endDate"> ~ {{ formatDate(item.endDate) }}</span>
                <span v-else-if="item.startDate"> 起（無限期）</span>
              </template>
              <span v-else class="tier-names">
                <i class="ri-vip-crown-line"></i>
                {{ getTierNames(item.tierIds) }}
              </span>
            </td>
            <td>
              <span :class="['status-badge', item.status, { 'line-default': item.isLineDefault }]">
                {{ statusLabel(item.status, item.isLineDefault) }}
              </span>
              <span v-if="item.isBaseMenu" class="base-menu-badge">
                <i class="ri-home-heart-fill"></i> 基本
              </span>
            </td>
            <td class="action-cell">
              <div class="action-cell-inner">
                <router-link :to="`/client/line/rich-menu/${item.id}/edit`" class="btn btn-sm btn-primary">
                  <i class="ri-edit-line"></i> 編輯
                </router-link>
                <button
                  v-if="item.status !== 'active'"
                  class="btn btn-sm btn-deploy"
                  @click="handleActivate(item)"
                  :disabled="deploying === item.id"
                >
                  <i :class="deploying === item.id ? 'ri-loader-4-line spin' : 'ri-checkbox-circle-line'"></i>
                  {{ deploying === item.id ? '啟用中' : '啟用' }}
                </button>
                <template v-else>
                  <button
                    v-if="!item.isLineDefault"
                    class="btn btn-sm btn-apply"
                    @click="handleApplyAsDefault(item)"
                    :disabled="deploying === item.id"
                  >
                    <i :class="deploying === item.id ? 'ri-loader-4-line spin' : 'ri-broadcast-line'"></i>
                    {{ deploying === item.id ? '套用中' : '立即套用' }}
                  </button>
                  <button
                    :class="['btn', 'btn-sm', item.isBaseMenu ? 'btn-base-active' : 'btn-base']"
                    @click="handleToggleBaseMenu(item)"
                    :disabled="deploying === item.id"
                  >
                    <i :class="deploying === item.id ? 'ri-loader-4-line spin' : (item.isBaseMenu ? 'ri-home-heart-fill' : 'ri-home-heart-line')"></i>
                    {{ item.isBaseMenu ? '基本選單' : '設為基本選單' }}
                  </button>
                  <button
                    class="btn btn-sm btn-undeploy"
                    @click="handleUndeploy(item)"
                    :disabled="deploying === item.id"
                  >
                    <i :class="deploying === item.id ? 'ri-loader-4-line spin' : 'ri-stop-circle-line'"></i>
                    {{ deploying === item.id ? '處理中' : '轉為草稿' }}
                  </button>
                </template>
                <button class="btn btn-sm btn-danger" @click="handleDelete(item)">
                  <i class="ri-delete-bin-line"></i> 刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="empty-state">
        <i class="ri-layout-grid-line"></i>
        <p>尚未建立任何圖文選單</p>
        <router-link to="/client/line/rich-menu/create" class="btn btn-primary">
          <i class="ri-add-line"></i> 新增第一個圖文選單
        </router-link>
      </div>
    </div>

    <!-- 分頁 -->
    <div v-show="activeTab === 'list'" v-if="pagination && pagination.totalPages > 1" class="pagination">
      <button class="page-btn" :disabled="pagination.page <= 1" @click="goPage(pagination.page - 1)">
        <i class="ri-arrow-left-s-line"></i>
      </button>
      <template v-for="p in paginationPages" :key="p">
        <span v-if="p === '...'" class="page-ellipsis">…</span>
        <button
          v-else
          :class="['page-btn', { active: p === pagination.page }]"
          @click="goPage(p)"
        >{{ p }}</button>
      </template>
      <button class="page-btn" :disabled="pagination.page >= pagination.totalPages" @click="goPage(pagination.page + 1)">
        <i class="ri-arrow-right-s-line"></i>
      </button>
      <span class="page-info">第 {{ pagination.page }} / {{ pagination.totalPages }} 頁，共 {{ pagination.totalItems }} 筆</span>
    </div>

    <!-- 操作記錄 Tab -->
    <div v-show="activeTab === 'logs'" class="logs-panel">
      <div class="logs-toolbar">
        <select v-model="logPanel.lineConfigId" @change="loadLogs" class="form-control form-control-sm">
          <option value="">全部 LINE 帳號</option>
          <option v-for="c in lineConfigs" :key="c.id" :value="c.id">{{ c.configName }}</option>
        </select>
        <button class="btn btn-secondary btn-sm" @click="loadLogs">
          <i class="ri-refresh-line"></i> 重新整理
        </button>
      </div>

      <div v-if="logPanel.loading" class="logs-loading">
        <i class="ri-loader-4-line spin"></i> 載入中...
      </div>
      <div v-else-if="logPanel.items.length === 0" class="logs-empty">
        <i class="ri-history-line"></i>
        <p>尚無操作記錄</p>
      </div>
      <table v-else class="logs-table">
        <thead>
          <tr>
            <th>時間</th>
            <th>LINE 帳號</th>
            <th>選單名稱</th>
            <th>操作</th>
            <th>觸發方式</th>
            <th>備註</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logPanel.items" :key="log.id">
            <td class="log-time">{{ formatLogTime(log.createdAt) }}</td>
            <td class="log-account">{{ lineConfigName(log.lineConfigId) }}</td>
            <td class="log-name">{{ log.groupName }}</td>
            <td>
              <span :class="['log-action-badge', log.action]">
                {{ logActionLabel(log.action) }}
              </span>
            </td>
            <td>
              <span :class="['log-trigger-badge', log.triggeredBy]">
                <i :class="log.triggeredBy === 'manual' ? 'ri-user-line' : 'ri-robot-line'"></i>
                {{ log.triggeredBy === 'manual' ? '手動' : '系統排程' }}
              </span>
            </td>
            <td class="log-note">{{ log.note || '—' }}</td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="logPanel.total > logPanel.pageSize"
        :page="logPanel.page"
        :total-pages="Math.ceil(logPanel.total / logPanel.pageSize)"
        :total="logPanel.total"
        @change="(p) => { logPanel.page = p; loadLogs(); }"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { lineRichMenuService } from '@/api/modules/lineRichMenu.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import Swal from 'sweetalert2';
import { showSuccess, showError, showConfirm, showDeleteConfirm } from '@/utils/swal.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';

export default {
  name: 'LineRichMenuIndex',
  data() {
    return {
      introOpen: false,
      items: [],
      allMemberItems: [],   // 所有會員模式選單（不分頁，用於完成度計算）
      pagination: null,
      loading: true,
      deploying: null,
      batchDeploying: false,
      deployJob: null,
      deployPollTimer: null,
      lineConfigs: [],
      memberTiers: [],
      richMenuMode: '',   // '' | 'schedule' | 'member'
      activeTab: 'list',
      logPanel: {
        loading: false,
        items: [],
        total: 0,
        page: 1,
        pageSize: 30,
        lineConfigId: '',
      },
      filters: {
        keyword: '',
        status: '',
        lineConfigId: '',
        displayMode: '',
        page: 1,
        pageSize: 20
      }
    };
  },
  async created() {
    await Promise.all([this.loadLineConfigs(), this.loadMemberTiers(), this.loadLineMode()]);
    await Promise.all([this.loadList(), this.loadAllMemberItems()]);
  },
  beforeUnmount() {
    this._stopPolling();
  },
  computed: {
    currentMenu() {
      return this.items.find(item => item.isLineDefault) || null;
    },
    deployJobHasError() {
      if (!this.deployJob?.group_results) return false;
      return this.deployJob.group_results.some(r => r.status === 'error');
    },
    memberCompleteness() {
      if (this.richMenuMode !== 'member') return null;

      // 已有選單所覆蓋的 tier IDs 集合
      const coveredTierIds = new Set();
      let hasNonMemberMenu = false;

      for (const item of this.allMemberItems) {
        const tiers = item.tierIds || [];
        if (tiers.length === 0) {
          hasNonMemberMenu = true;
        } else {
          tiers.forEach(id => coveredTierIds.add(String(id)));
        }
      }

      const tierChecks = this.memberTiers.map(tier => ({
        id: tier.id,
        name: tier.tierName || tier.name,
        done: coveredTierIds.has(String(tier.id)),
      }));

      const total = tierChecks.length + 1; // tiers + 非會員
      const doneCount = tierChecks.filter(t => t.done).length + (hasNonMemberMenu ? 1 : 0);
      const allDone = doneCount === total;

      return { tierChecks, hasNonMemberMenu, total, doneCount, allDone };
    },
    paginationPages() {
      if (!this.pagination) return [];
      const { page, totalPages } = this.pagination;
      const pages = [];
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        if (page > 3) pages.push('...');
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
        if (page < totalPages - 2) pages.push('...');
        pages.push(totalPages);
      }
      return pages;
    }
  },
  methods: {
    async loadLineConfigs() {
      try {
        const res = await lineConfigApi.list({ pageSize: 100 });
        this.lineConfigs = res?.data?.items || res?.data || [];
        if (this.lineConfigs.length === 1) {
          this.filters.lineConfigId = this.lineConfigs[0].id;
        }
      } catch {}
    },
    async loadList() {
      this.loading = true;
      try {
        const res = await lineRichMenuService.list(this.filters);
        const body = res.data;
        // 支援兩種回應結構：{ data: { items } } 和 { items }
        const payload = body?.data ?? body;
        this.items = payload?.items || [];
        this.pagination = payload?.pagination || null;
      } catch (e) {
        showError('載入失敗', e.response?.data?.message || e.message || '');
      } finally {
        this.loading = false;
      }
    },
    async handleActivate(item) {
      const isPastEndDate = item.displayMode === 'schedule' && item.endDate && new Date(item.endDate) < new Date();
      const { isConfirmed: confirmed } = await Swal.fire({
        icon: 'question',
        title: `啟用「${item.name}」`,
        html: `<div style="text-align:left;line-height:1.8;font-size:14px;">
          ${isPastEndDate ? `<p style="color:#dc2626;font-weight:600;margin-bottom:8px;">⚠️ 此選單的有效期間已結束（${this.formatDate(item.endDate)}）。啟用後若不更新結束日期，排程系統將立即再次下架此選單，且無法取代基本選單。</p>` : ''}
          <p>• 啟用後可在編輯頁按「立即套用」指派給所有好友。</p>
          <p>• 或者您可以設定顯示期間，期間到會自動套用。</p>
        </div>`,
        showCancelButton: true,
        confirmButtonText: '確定啟用',
        cancelButtonText: '取消',
        confirmButtonColor: '#06C755',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
      });
      if (!confirmed) return;
      this.deploying = item.id;
      try {
        await lineRichMenuService.activate(item.id);
        showSuccess('啟用成功', '圖文選單已在 LINE 建立，請至編輯頁按「立即套用」設為預設。');
        await this.loadList();
      } catch (e) {
        showError('啟用失敗', e.response?.data?.message || e.message || '');
      } finally {
        this.deploying = null;
      }
    },
    async handleUndeploy(item) {
      const confirmed = await showConfirm(
        `轉為草稿「${item.name}」`,
        '取消後選單將從 LINE 移除。',
        '確定轉為草稿', '返回', 'warning'
      );
      if (!confirmed) return;
      this.deploying = item.id;
      try {
        await lineRichMenuService.undeploy(item.id);
        showSuccess('已轉為草稿', '');
        await this.loadList();
      } catch (e) {
        showError('轉為草稿失敗', e.response?.data?.message || e.message || '');
      } finally {
        this.deploying = null;
      }
    },
    async handleApplyAsDefault(item) {
      // 排程模式：若結束時間已過，套用後排程系統將立即下架，無法取代基本選單
      if (item.displayMode === 'schedule' && item.endDate && new Date(item.endDate) < new Date()) {
        await Swal.fire({
          icon: 'warning',
          title: '無法套用',
          html: `此圖文選單的有效期間已結束（${this.formatDate(item.endDate)}），<br>套用後排程系統將立即再次將其下架，<strong>無法取代基本選單</strong>。<br><br>請至編輯頁更新或移除結束日期後再套用。`,
          confirmButtonText: '我知道了',
          confirmButtonColor: '#6c757d',
        });
        return;
      }
      const modeLabel = item.displayMode === 'schedule' ? '設為 LINE 頻道預設選單' : '連結到符合等級的好友';
      const confirmed = await showConfirm(
        `立即套用「${item.name}」`,
        `將此圖文選單${modeLabel}。若有其他有效的排程選單，將自動改回草稿。`,
        '確定套用', '取消', 'question'
      );
      if (!confirmed) return;
      this.deploying = item.id;
      try {
        await lineRichMenuService.applyAsDefault(item.id);
        showSuccess('已立即套用', item.displayMode === 'schedule' ? '此選單已設為 LINE 頻道預設選單。' : '已連結到符合等級的好友。');
        await this.loadList();
      } catch (e) {
        showError('套用失敗', e.response?.data?.message || e.message || '');
      } finally {
        this.deploying = null;
      }
    },
    async handleToggleBaseMenu(item) {
      if (item.isBaseMenu) {
        const confirmed = await showConfirm(
          '取消基本選單',
          `取消「${item.name}」的基本選單設定後，排程選單到期時將不再自動 fallback。`,
          '確定取消', '返回', 'warning'
        );
        if (!confirmed) return;
        this.deploying = item.id;
        try {
          await lineRichMenuService.unsetBaseMenu(item.id);
          showSuccess('已取消基本選單', '');
          await this.loadList();
        } catch (e) {
          showError('操作失敗', e.response?.data?.message || e.message || '');
        } finally {
          this.deploying = null;
        }
      } else {
        const currentBaseMenu = this.items.find(menu => menu.isBaseMenu && menu.id !== item.id);
        const replaceNotice = currentBaseMenu
          ? `目前的基本選單「${currentBaseMenu.name}」將被取代。`
          : '若已有基本選單，將直接由此選單取代。';
        const confirmed = await showConfirm(
          `設為基本選單`,
          `將「${item.name}」設為基本選單，並立即取代 LINE 目前的預設選單。${replaceNotice}`,
          '確定取代', '取消', 'question'
        );
        if (!confirmed) return;
        this.deploying = item.id;
        try {
          await lineRichMenuService.setBaseMenu(item.id);
          showSuccess('基本選單已變更', '新選單已立即取代原本的 LINE 預設選單。');
          await this.loadList();
        } catch (e) {
          showError('設定失敗', e.response?.data?.message || e.message || '');
        } finally {
          this.deploying = null;
        }
      }
    },
    async handleDelete(item) {
      const confirmed = await showDeleteConfirm(item.name, '若已部署，系統會先自動取消部署。');
      if (!confirmed) return;
      try {
        await lineRichMenuService.remove(item.id);
        showSuccess('刪除成功', '');
        await this.loadList();
      } catch (e) {
        showError('刪除失敗', e.response?.data?.message || e.message || '');
      }
    },
    async handleBatchDeploy() {
      const confirmed = await showConfirm(
        '批次部署所有會員模式選單',
        '系統將依各選單設定的等級，自動將選單指派給符合的 LINE 好友。好友數量龐大時，用戶指派會在背景持續執行。',
        '確定部署', '取消', 'question'
      );
      if (!confirmed) return;
      this.batchDeploying = true;
      try {
        const lineConfigId = this.filters.lineConfigId || this.lineConfigs[0]?.id;
        if (!lineConfigId) { showError('請先選擇 LINE 帳號', ''); return; }

        const res = await lineRichMenuService.batchDeployMember(lineConfigId);
        const body = res.data ?? res;
        const data = body?.data ?? body;

        if (!data?.jobId) {
          showError('沒有可部署的選單', '');
          return;
        }

        // 先顯示 processing 狀態，再開始輪詢
        this.deployJob = {
          status: 'processing',
          total_groups: data.groups || 0,
          done_groups: 0,
          processed_users: 0,
          group_results: [],
        };
        this._startPolling(data.jobId);
      } catch (e) {
        showError('批次部署失敗', e.response?.data?.message || e.message || '');
      } finally {
        this.batchDeploying = false;
      }
    },
    _startPolling(jobId) {
      this._stopPolling();
      let interval = 2000;
      const poll = async () => {
        try {
          const res = await lineRichMenuService.getDeployJob(jobId);
          const body = res.data ?? res;
          const job = body?.data ?? body;
          this.deployJob = job;
          if (job.status === 'completed') {
            this._stopPolling();
            await this.loadAllMemberItems();
            return;
          }
          // 指數退避：最多 10s
          interval = Math.min(interval * 1.3, 10000);
        } catch {}
        this.deployPollTimer = setTimeout(poll, interval);
      };
      this.deployPollTimer = setTimeout(poll, interval);
    },
    _stopPolling() {
      if (this.deployPollTimer) {
        clearTimeout(this.deployPollTimer);
        this.deployPollTimer = null;
      }
    },
    closeBatchModal() {
      this._stopPolling();
      this.deployJob = null;
    },
    async loadAllMemberItems() {
      if (this.richMenuMode !== 'member') return;
      try {
        const res = await lineRichMenuService.list({ displayMode: 'member', pageSize: 200 });
        const body = res.data;
        const payload = body?.data ?? body;
        this.allMemberItems = payload?.items || [];
      } catch {}
    },
    search() {
      this.filters.page = 1;
      this.loadList();
      this.loadAllMemberItems();
    },
    goPage(p) {
      this.filters.page = p;
      this.loadList();
    },
    async loadMemberTiers() {
      try {
        const res = await memberTierService.getTiers();
        const body = res.data ?? res;
        this.memberTiers = Array.isArray(body) ? body : (body?.items || body?.data || []);
      } catch {}
    },
    async loadLineMode() {
      try {
        const res = await clientSettingsService.get(NAMESPACES.LINE);
        const mode = res?.data?.settings?.richMenuMode;
        if (mode === 'schedule' || mode === 'member') {
          this.richMenuMode = mode;
          this.filters.displayMode = mode;
        }
      } catch {}
    },
    getConfigName(configId) {
      return this.lineConfigs.find(c => c.id === configId)?.configName || '—';
    },
    getTierNames(tierIds) {
      if (!tierIds?.length) return '非會員';
      return tierIds.map(id => {
        const tier = this.memberTiers.find(t => String(t.id) === String(id));
        return tier?.tierName || tier?.name || id;
      }).join('、');
    },
    statusLabel(s, isLineDefault) {
      if (s === 'active' && isLineDefault) return '目前顯示';
      return { draft: '草稿', active: '已建立', expired: '已到期' }[s] || s;
    },
    formatCount(value) {
      return Number(value || 0).toLocaleString('zh-TW');
    },
    async switchToLogs() {
      this.activeTab = 'logs';
      this.logPanel.page = 1;
      this.logPanel.lineConfigId = this.filters.lineConfigId || '';
      await this.loadLogs();
    },
    async loadLogs() {
      this.logPanel.loading = true;
      try {
        const res = await lineRichMenuService.getLogs({
          lineConfigId: this.logPanel.lineConfigId || undefined,
          page: this.logPanel.page,
          pageSize: this.logPanel.pageSize,
        });
        const body = res.data;
        const payload = body?.data ?? body;
        this.logPanel.items = payload?.items || [];
        this.logPanel.total = payload?.total || 0;
      } catch (e) {
        showError('載入記錄失敗', e.response?.data?.message || '');
      } finally {
        this.logPanel.loading = false;
      }
    },
    lineConfigName(id) {
      const c = this.lineConfigs.find(c => c.id === id);
      return c ? c.configName : id;
    },
    formatLogTime(d) {
      if (!d) return '';
      return new Date(d).toLocaleString('zh-TW', {
        month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    },
    logActionLabel(action) {
      return {
        apply_manual:        '手動套用',
        apply_schedule:      '排程自動套用',
        apply_base_fallback: '基本選單備援',
        expire:              '排程到期下架',
      }[action] || action;
    },

    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d);
      const date = dt.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
      const hour = String(dt.getHours()).padStart(2, '0');
      return `${date} ${hour}:00`;
    },
    remainingTime(d) {
      if (!d) return null;
      const diff = new Date(d) - Date.now();
      if (diff <= 0) return '已到期';
      const totalMinutes = Math.floor(diff / 60000);
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      if (days > 0) return `剩 ${days} 天 ${hours} 小時`;
      if (hours > 0) return `剩 ${hours} 小時 ${minutes} 分`;
      return `剩 ${minutes} 分鐘`;
    },
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.rich-menu-intro {
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #e7f1ff;
  border: 1px solid #b6d4fe;
  border-radius: 8px;
  color: #0a58ca;
}

.rich-menu-intro.is-expanded {
  padding: 16px 20px;
}

.rich-menu-intro .intro-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.rich-menu-intro .intro-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.rich-menu-intro .intro-title i {
  font-size: 18px;
}

.rich-menu-intro .intro-toggle-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 13px;
  color: #084298;
}

.rich-menu-intro .intro-body {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(10, 88, 202, 0.15);
}

.rich-menu-intro .intro-text {
  margin: 0 0 10px 0;
  font-size: 14px;
  line-height: 1.5;
}

.rich-menu-intro .intro-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.6;
}

.rich-menu-intro .intro-list li {
  margin-bottom: 6px;
}

.rich-menu-intro .intro-list li:last-child {
  margin-bottom: 0;
}

.table-wrapper table {
  min-width: 1050px;
}

.name-cell { font-weight: 500; }
.menu-thumb-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.menu-thumb {
  width: 64px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  flex-shrink: 0;
  background: #f8f9fa;
  display: block;
}
.menu-thumb.full  { height: 43px; }
.menu-thumb.half  { height: 22px; }
.menu-thumb-placeholder {
  width: 64px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ced4da;
  font-size: 16px;
  flex-shrink: 0;
}
.menu-thumb-placeholder.full { height: 43px; }
.menu-thumb-placeholder.half { height: 22px; }
.center-cell { text-align: center; }
.clicks-cell strong { display: block; color: #15803d; font-size: 14px; }
.clicks-cell > span:not(.tracking-disabled-badge) { color: #9ca3af; font-size: 11px; }
.tracking-disabled-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.date-cell { font-size: 13px; white-space: nowrap; }
.base-period-label { color: #b45309; font-weight: 600; }
.text-muted { color: #9ca3af; }
.mode-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
}
.mode-banner.schedule {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.mode-banner.member {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}
.mode-banner i { font-size: 16px; flex-shrink: 0; }
.btn-batch-deploy {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 14px; border-radius: 6px; border: 1px solid currentColor;
  background: rgba(255,255,255,.3); color: inherit;
  font-size: 13px; font-weight: 500; cursor: pointer;
  white-space: nowrap; flex-shrink: 0; transition: background .15s;
}
.btn-batch-deploy:hover:not(:disabled) { background: rgba(255,255,255,.5); }
.btn-batch-deploy:disabled { opacity: .6; cursor: not-allowed; }

/* 會員模式完成度卡片 */
.completeness-card {
  border-radius: 10px;
  border: 1px solid;
  padding: 14px 18px;
  margin-bottom: 16px;
  font-size: 13px;
}
.completeness-card.all-done {
  background: #f0fdf4;
  border-color: #86efac;
  color: #14532d;
}
.completeness-card.has-missing {
  background: #fffbeb;
  border-color: #fcd34d;
  color: #78350f;
}
.completeness-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin-bottom: 6px;
}
.completeness-header i { font-size: 16px; flex-shrink: 0; }
.completeness-title { flex: 1; }
.completeness-count {
  font-size: 12px;
  font-weight: 500;
  opacity: 0.75;
  flex-shrink: 0;
}
.completeness-desc {
  font-size: 12px;
  opacity: 0.8;
  margin-bottom: 10px;
  line-height: 1.6;
}
.completeness-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-top: 8px;
}
.completeness-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.completeness-item.done { background: #dcfce7; color: #15803d; }
.completeness-item.missing { background: #fef9c3; color: #92400e; }
.completeness-item i { font-size: 13px; }
.item-hint { font-weight: 400; opacity: 0.75; }
.mode-banner span { flex: 1; }
.mode-banner-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 500;
  color: inherit;
  text-decoration: none;
  opacity: 0.75;
  white-space: nowrap;
  flex-shrink: 0;
}
.mode-banner-link:hover { opacity: 1; text-decoration: underline; }

.tier-names {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #92400e;
}
.tier-names i { font-size: 12px; }

.mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.mode-badge.schedule { background: #eff6ff; color: #3b82f6; }
.mode-badge.member   { background: #fef9c3; color: #92400e; }

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}
.status-badge.draft   { background: #f3f4f6; color: #6b7280; }
.status-badge.active  { background: #dcfce7; color: #166534; }
.status-badge.active.line-default { background: #7c3aed; color: #fff; }
.status-badge.expired { background: #fee2e2; color: #991b1b; }

.action-cell-inner { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }

.btn {
  display: inline-flex; align-items: center; gap: 5px;
  border: none; border-radius: 7px; font-weight: 500; cursor: pointer;
  text-decoration: none; white-space: nowrap; transition: background 0.15s, opacity 0.15s;
}
.btn:disabled { opacity: .55; cursor: not-allowed; }
.btn-sm { padding: 5px 11px; font-size: 12px; }

.btn-deploy {
  background: #f0fdf4; color: #16a34a;
  border: 1px solid #86efac;
}
.btn-deploy:hover:not(:disabled) { background: #dcfce7; }

.btn-undeploy {
  background: #fffbeb; color: #b45309;
  border: 1px solid #fcd34d;
}
.btn-undeploy:hover:not(:disabled) { background: #fef3c7; }

.btn-apply {
  background: #f5f3ff; color: #7c3aed;
  border: 1px solid #c4b5fd;
}
.btn-apply:hover:not(:disabled) { background: #ede9fe; }

.btn-base {
  background: #fff; color: #d97706;
  border: 1px solid #fbbf24;
}
.btn-base:hover:not(:disabled) { background: #fffbeb; border-color: #f59e0b; }
.btn-base-active {
  background: #f59e0b; color: #fff;
  border: 1px solid #f59e0b;
}
.btn-base-active:hover:not(:disabled) { background: #d97706; }

.base-menu-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}

/* ─── 目前顯示中卡片 ─────────────────────────── */
.current-menu-card {
  border: 2px solid #22c55e;
  border-radius: 12px;
  background: #f0fdf4;
  margin-bottom: 16px;
  overflow: hidden;
}
.current-menu-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  background: #22c55e;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
}
.current-menu-body {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
}
.current-menu-thumb-wrap {
  flex-shrink: 0;
}
.current-menu-thumb {
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #bbf7d0;
}
.current-menu-thumb.full { width: 72px; height: 36px; }
.current-menu-thumb.half { width: 72px; height: 18px; }
.current-menu-thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 36px;
  border-radius: 6px;
  background: #dcfce7;
  color: #86efac;
  font-size: 20px;
}
.current-menu-info {
  flex: 1;
  min-width: 0;
}
.current-menu-name {
  font-size: 15px;
  font-weight: 700;
  color: #15803d;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.current-menu-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 13px;
  color: #4b5563;
}
.current-menu-meta .dot { color: #9ca3af; }
.current-menu-meta .base-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
}
.current-menu-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
}
.date-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #374151;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 2px 8px;
}
.date-item i { color: #16a34a; font-size: 13px; }
.date-label { color: #6b7280; font-weight: 500; }
.date-value { font-weight: 600; color: #15803d; }
.remaining-badge {
  margin-left: 4px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #fee2e2;
  color: #dc2626;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.current-menu-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

/* ─── Log Modal ─────────────────────────────────────── */
/* 頁面 Tab */
.page-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 20px;
}
.page-tab {
  padding: 10px 20px;
  border: none; background: transparent; cursor: pointer;
  font-size: 14px; color: #6b7280; font-weight: 500;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  display: inline-flex; align-items: center; gap: 6px;
  transition: color 0.15s, border-color 0.15s;
}
.page-tab:hover { color: #06C755; }
.page-tab.active { color: #06C755; border-bottom-color: #06C755; font-weight: 600; }

/* 操作記錄面板 */
.logs-panel {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}
.logs-toolbar {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #f9fafb;
}
.logs-toolbar .form-control-sm { min-width: 160px; max-width: 320px; font-size: 13px; }
.logs-loading, .logs-empty {
  padding: 60px 20px; text-align: center; color: #9ca3af;
}
.logs-empty i { font-size: 40px; display: block; margin-bottom: 10px; }
.logs-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
}
.logs-table th {
  padding: 10px 16px; background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  text-align: left; font-weight: 600; color: #6b7280;
  white-space: nowrap;
}
.logs-table td {
  padding: 11px 16px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}
.logs-table tr:last-child td { border-bottom: none; }
.logs-table tr:hover td { background: #f9fafb; }
.log-time { color: #6b7280; white-space: nowrap; font-size: 12px; }
.log-account { color: #6b7280; font-size: 12px; white-space: nowrap; }
.log-name { font-weight: 500; color: #111827; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.log-note { color: #9ca3af; font-size: 12px; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.log-action-badge {
  display: inline-block; padding: 2px 9px; border-radius: 10px;
  font-size: 11px; font-weight: 600; white-space: nowrap;
}
.log-action-badge.apply_manual        { background: #dbeafe; color: #1d4ed8; }
.log-action-badge.apply_schedule      { background: #d1fae5; color: #065f46; }
.log-action-badge.apply_base_fallback { background: #fef3c7; color: #92400e; }
.log-action-badge.expire              { background: #fee2e2; color: #991b1b; }
.log-trigger-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 10px;
  font-size: 11px; font-weight: 500;
}
.log-trigger-badge.manual    { background: #f3f4f6; color: #374151; }
.log-trigger-badge.scheduler { background: #ede9fe; color: #5b21b6; }
.logs-pagination {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 12px 16px; border-top: 1px solid #e5e7eb;
  font-size: 13px; color: #6b7280;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #9ca3af;
}
.empty-state i { font-size: 48px; display: block; margin-bottom: 12px; }
.empty-state p { margin-bottom: 16px; }

.pagination {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 20px;
}
.page-btn {
  min-width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn.active { background: #06C755; color: #fff; border-color: #06C755; }
.page-ellipsis { display: inline-flex; align-items: center; padding: 0 4px; color: #9ca3af; font-size: 13px; }
.page-info { font-size: 12px; color: #9ca3af; margin-left: 8px; align-self: center; }

.loading-state {
  padding: 60px;
  text-align: center;
  color: #9ca3af;
}
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* 批次部署進度 Modal */
.batch-result-backdrop {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.55);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.batch-result-modal {
  background: #fff; border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
  width: min(480px, 100%); max-height: 80vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.batch-result-header {
  display: flex; align-items: center; gap: 10px;
  padding: 20px 20px 12px;
}
.batch-result-header h3 { font-size: 16px; font-weight: 600; margin: 0; }
.success-icon { font-size: 24px; color: #22c55e; }
.warn-icon    { font-size: 24px; color: #f59e0b; }

/* 部署階段進度列 */
.deploy-stage {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 20px; font-size: 13px; color: #374151;
}
.deploy-stage:first-of-type { padding-top: 0; }
.stage-label { color: #6b7280; }
.stage-badge {
  font-size: 12px; font-weight: 600;
  padding: 2px 8px; border-radius: 20px;
}
.stage-badge.done    { background: #f0fdf4; color: #15803d; }
.stage-badge.pending { background: #eff6ff; color: #1d4ed8; }
.deploy-stage-note {
  margin: 2px 20px 10px;
  font-size: 12px; color: #6b7280;
  background: #f8f9fa; border-radius: 6px;
  padding: 8px 10px;
  display: flex; gap: 6px; align-items: flex-start;
}
.deploy-stage-note i { flex-shrink: 0; margin-top: 1px; }

.batch-result-list {
  overflow-y: auto; padding: 12px 20px;
  display: flex; flex-direction: column; gap: 6px;
  border-top: 1px solid #f3f4f6;
}
.batch-result-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; border-radius: 8px; font-size: 13px;
}
.batch-result-item.success { background: #f0fdf4; color: #15803d; }
.batch-result-item.error   { background: #fef2f2; color: #dc2626; }
.batch-result-item.pending { background: #f9fafb; color: #6b7280; }
.batch-result-item i { font-size: 15px; flex-shrink: 0; }
.r-name { font-weight: 500; flex: 1; }
.r-msg  { font-size: 11px; opacity: .75; }
.batch-result-footer {
  padding: 12px 20px;
  border-top: 1px solid #f3f4f6;
  display: flex; justify-content: flex-end; gap: 8px;
}
.btn-secondary {
  background: #f3f4f6; color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px; padding: 7px 16px;
  font-size: 13px; cursor: pointer;
}
.btn-secondary:hover { background: #e5e7eb; }
</style>
